import { useFormContext } from '@calendly/form';
import { useOnValueChange } from '@calendly/hooks';
import { I18n } from '@calendly/i18n';
import { AddMiniIcon } from '@calendly/ui/components/icons';
import { MinimalButton } from '@calendly/ui/components/minimal-button';
import { BodyText } from '@calendly/ui/components/typography';
import { colorTextDisabled } from '@calendly/ui/theme';
import classNames from 'classnames';
import { difference } from 'lodash-es';
import { useCallback, useEffect, useMemo, useRef, useState, forwardRef } from 'react';
import { flushSync } from 'react-dom';
import { useTrackAvailabilityInteraction } from 'hooks/analytics_events/use_track_availability_interaction';
import { useDebounce } from 'hooks/useDebounce';
import { LIMIT_TYPES } from './constants';
import { LimitField } from './limit_field';
const flexContainerCss = "fb4lksv";
const FlexContainer = forwardRef((props, ref) => <div ref={ref} {...props} className={classNames(flexContainerCss, props.className)} />);
const styledLimitFieldCss = "s1grsm24";
const StyledLimitField = forwardRef((props, ref) => <LimitField ref={ref} {...props} className={classNames(styledLimitFieldCss, props.className)} />);
const addMinimalButtonCss = "a1vsbwbm";
const AddMinimalButton = forwardRef((props, ref) => <MinimalButton ref={ref} {...props} className={classNames(addMinimalButtonCss, props.className)} />);
const AddButton = ({
  children,
  ...props
}) => <AddMinimalButton {...props} spacingTop={16}>
    <AddMiniIcon />
    {children}
  </AddMinimalButton>;
const disabledLimitFieldCss = "d1qguchk";
const DisabledLimitField = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  return <Component ref={ref} {...restProps} className={classNames(disabledLimitFieldCss, props.className)} />;
});
const EventLimitsLocked = () => {
  const {
    values: {
      dailyLimit,
      weeklyLimit,
      monthlyLimit
    }
  } = useFormContext();
  const limits = [['day', dailyLimit], ['week', weeklyLimit], ['month', monthlyLimit]];
  const filteredLimits = limits.filter(([_, limitValue]) => limitValue !== undefined);
  const showButton = filteredLimits.length < 3;
  const addFirstLimitText = I18n.t('publisher.availability_page.advanced_settings.meeting_limits.add_limit');
  const hasLimits = filteredLimits.length > 0;
  const buttonText = !hasLimits ? addFirstLimitText : I18n.t('publisher.availability_page.advanced_settings.meeting_limits.add_another_limit');
  const formatLimit = (limitType, limitValue) => {
    const meetingsPer = I18n.t('publisher.event_type_editor.sections.scheduling_settings.event_limits.max_meetings.meetings_per');
    const periodType = I18n.t(`publisher.event_type_editor.sections.scheduling_settings.event_limits.max_meetings.summary.${limitType}`);
    const notActive = I18n.t('publisher.event_type_editor.sections.scheduling_settings.event_limits.max_meetings.not_active');
    return `${limitValue} ${meetingsPer} ${periodType} ${notActive}`;
  };
  return <>
      {hasLimits && <FlexContainer>
          <BodyText weight="semibold" size="medium" color={colorTextDisabled}>
            {I18n.t('publisher.availability_page.advanced_settings.meeting_limits.schedule_no_more_than')}
          </BodyText>
          {filteredLimits.map(([limitType, limitValue]) => <DisabledLimitField key={limitType}>{formatLimit(limitType, limitValue)}</DisabledLimitField>)}
        </FlexContainer>}
      {showButton && <AddButton disabled>
          {buttonText}
        </AddButton>}
    </>;
};
const EventLimitsFields = () => {
  const {
    values: {
      dailyLimit,
      weeklyLimit,
      monthlyLimit
    },
    setFieldValue,
    submitForm,
    validateForm,
    dirty
  } = useFormContext();
  const limitTypes = useMemo(() => ({
    dailyLimit,
    weeklyLimit,
    monthlyLimit
  }), [dailyLimit, weeklyLimit, monthlyLimit]);
  const debouncedLimitTypes = useDebounce(limitTypes, 500);
  const trackAddLimit = useTrackAvailabilityInteraction({
    action: 'add_a_meeting_limit'
  });
  const trackUpdateLimit = useTrackAvailabilityInteraction({
    action: 'update_global_limit'
  });
  const skipNextDebouncedSubmitRef = useRef(false);
  const onFormSubmit = async () => {
    if (!dirty) return;
    await submitForm();
    const errors = await validateForm();
    if (Object.keys(errors).length === 0) {
      trackUpdateLimit();
    }
  };
  const onLimitRemoved = useCallback(async () => {
    skipNextDebouncedSubmitRef.current = true;
    flushSync(() => {});
    await submitForm();
    const errors = await validateForm();
    if (Object.keys(errors).length === 0) {
      trackUpdateLimit();
    }
  }, [submitForm, validateForm, trackUpdateLimit]);
  useOnValueChange(debouncedLimitTypes, () => {
    if (skipNextDebouncedSubmitRef.current) {
      skipNextDebouncedSubmitRef.current = false;
      return;
    }
    onFormSubmit();
  });
  const [observableLimitTypes, setObservableLimitTypes] = useState(LIMIT_TYPES.filter(type => limitTypes[type] !== undefined));
  const showButton = observableLimitTypes.length < 3;
  const addFirstLimitText = I18n.t('publisher.availability_page.advanced_settings.meeting_limits.add_limit');
  const buttonText = observableLimitTypes.length === 0 ? addFirstLimitText : I18n.t('publisher.availability_page.advanced_settings.meeting_limits.add_another_limit');
  const [lastAddedType, setLastAddedType] = useState(null);
  const inputRefs = useRef({});
  const onAddLimit = useCallback(() => {
    const limitType = difference(LIMIT_TYPES, observableLimitTypes)[0];
    setObservableLimitTypes([...observableLimitTypes, limitType]);
    setFieldValue(limitType, '');
    setLastAddedType(limitType);
    trackAddLimit();
    flushSync(() => {});
    skipNextDebouncedSubmitRef.current = true;
    Promise.resolve(submitForm()).then(() => validateForm()).then(errors => {
      const err = errors ?? {};
      if (Object.keys(err).length === 0) {
        trackUpdateLimit();
      }
    });
  }, [observableLimitTypes, setFieldValue, submitForm, validateForm, trackAddLimit, trackUpdateLimit]);
  useEffect(() => {
    if (!lastAddedType) return;
    const el = inputRefs.current[lastAddedType];
    if (el && typeof el.focus === 'function') {
      el.focus();
      setLastAddedType(null);
    }
  }, [observableLimitTypes, lastAddedType]);
  return <>
      {!!observableLimitTypes.length && <FlexContainer>
          <BodyText weight="semibold" size="medium">
            {I18n.t('publisher.availability_page.advanced_settings.meeting_limits.schedule_no_more_than')}
          </BodyText>
          {observableLimitTypes.map(limitType => <StyledLimitField key={limitType} limitType={limitType} observableLimitTypes={observableLimitTypes} setObservableLimitTypes={setObservableLimitTypes} onLimitRemoved={onLimitRemoved} ref={el => {
        inputRefs.current[limitType] = el;
      }} />)}
        </FlexContainer>}
      {showButton && <AddButton onClick={onAddLimit}>
          {buttonText}
        </AddButton>}
    </>;
};
export const GlobalEventLimits = ({
  isLocked
}) => !isLocked ? <EventLimitsFields /> : <EventLimitsLocked />;

require("./global_event_limits.wyw-in-js.css!=!../../../../../node_modules/@wyw-in-js/webpack-loader/lib/outputCssLoader.js?cacheProvider=!./global_event_limits.js");